import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function QualitySafetyBox({ className = '', headerOnly }) {
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const location = useLocation();
  const isAboutPage = headerOnly ?? (location.pathname === '/om' || location.pathname === '/about' || location.pathname === '/om-mig');

  // Exact verbatim URL as required by STRICT ASSET URL RULE
  const certificateSrc = "https://assets.auxodev.dk/assets/9bc67d49-c1ad-4360-8231-e9811640bce9/bf29ebfa-ee9d-4bb1-9a79-b234d2ac128f.jpg";

  const checklistPoints = [
    "Journalføring og dokumentation efter gældende regler",
    "Tavshedspligt, samtykke og fortrolighed",
    "Persondata og datasikkerhed",
    "Håndtering af utilsigtede hændelser",
    "Faglig opdatering og løbende kvalitetsudvikling",
    "Information om behandling, rammer og klientens rettigheder",
    "Erhvervsansvarsforsikring med dækning for person- og tingskade"
  ];

  return (
    <div className={`relative overflow-hidden bg-white border border-[#5f7365] ${className}`}>
      {/* Rolig, organisk cirkelgrafik i baggrunden */}
      <div 
        className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full border border-[#EDE6D8]/70 pointer-events-none" 
        aria-hidden="true"
      />
      <div 
        className="absolute -right-12 -bottom-12 w-72 h-72 rounded-full border border-[#5f7365]/15 pointer-events-none" 
        aria-hidden="true"
      />
      <div 
        className="absolute right-0 bottom-0 w-48 h-48 rounded-full border border-[#5f7365]/20 pointer-events-none" 
        aria-hidden="true"
      />

      <div className={`p-6 sm:p-8 lg:p-10 ${isAboutPage ? '' : 'space-y-8'} relative z-10`}>
        {/* Header section: Text on left, action buttons on right */}
        <div className={`flex flex-col lg:flex-row lg:items-end justify-between gap-6 ${isAboutPage ? '' : 'pb-7 border-b border-[#5f7365]/30'}`}>
          <div className="space-y-3.5 max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs font-medium tracking-widest uppercase text-[#5f7365]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#5f7365]" />
              <span>Patientsikkerhed og faglig standard</span>
            </div>

            <h2 className="font-serif text-2xl sm:text-3xl text-[#2C3B32] font-normal tracking-tight">
              Kvalitet, sikkerhed og ansvar
            </h2>
            
            <p className="text-sm sm:text-base text-[#52685B] leading-relaxed font-light">
              I min psykologpraksis lægger jeg vægt på høj faglig kvalitet, patientsikkerhed og ansvarlig behandling.
            </p>

            {/* Official Certification Badges */}
            <div className="flex flex-wrap items-center gap-2.5 pt-1">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#FAF8F5] border border-[#EDE6D8] text-xs text-[#2C3B32] rounded-full">
                <i className="fa-solid fa-certificate text-[#5f7365] text-xs" aria-hidden="true" />
                <span className="font-medium">Autoriseret af Psykolognævnet</span>
              </div>
            </div>
          </div>

          {/* Buttons on the right: contextual and clean */}
          <div className="shrink-0 flex flex-wrap sm:flex-nowrap items-center gap-2.5 w-full lg:w-auto self-start lg:self-end">
            {!isAboutPage ? (
              <Link
                to="/om"
                className="flex-1 sm:flex-none text-center px-4 py-2.5 bg-white hover:bg-[#FAF8F5] text-[#2C3B32] border border-[#EDE6D8] hover:border-[#5f7365] text-xs font-medium tracking-wide transition-all inline-flex items-center justify-center gap-2 shadow-2xs"
              >
                <i className="fa-regular fa-user text-xs text-[#5f7365]" aria-hidden="true" />
                <span>Om mig</span>
              </Link>
            ) : (
              <Link
                to="/kontakt"
                className="flex-1 sm:flex-none text-center px-4 py-2.5 bg-white hover:bg-[#FAF8F5] text-[#2C3B32] border border-[#EDE6D8] hover:border-[#5f7365] text-xs font-medium tracking-wide transition-all inline-flex items-center justify-center gap-2 shadow-2xs"
              >
                <i className="fa-regular fa-envelope text-xs text-[#5f7365]" aria-hidden="true" />
                <span>Kontakt</span>
              </Link>
            )}
            <Link
              to="/cv"
              className="flex-1 sm:flex-none text-center px-4 py-2.5 bg-[#5f7365] hover:bg-[#4d6053] text-white text-xs font-medium tracking-wide transition-all inline-flex items-center justify-center gap-2 shadow-xs"
            >
              <i className="fa-regular fa-file-lines text-xs text-[#EAE3D5]" aria-hidden="true" />
              <span>Se fuldt CV</span>
              <i className="fa-regular fa-arrow-right text-[10px]" aria-hidden="true" />
            </Link>
          </div>
        </div>

        {/* Checklist section: modern, calm, and clearly structured */}
        {!isAboutPage && (
          <div className="pt-2">
            {/* List with calm, serene row design */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 sm:gap-3">
              {/* Punkt 1-7: Faglig kvalitet og rammer */}
              {checklistPoints.map((point, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-3 p-3.5 bg-[#FAF8F5]/60 hover:bg-[#FAF8F5] border border-[#EDE6D8]/80 hover:border-[#5f7365]/30 transition-all"
                >
                  <div className="w-6 h-6 rounded-full bg-[#5f7365]/10 text-[#5f7365] flex items-center justify-center shrink-0">
                    <i className="fa-regular fa-check text-xs" aria-hidden="true" />
                  </div>
                  <p className="text-xs sm:text-sm font-medium text-[#2C3B32] leading-snug">
                    {point}
                  </p>
                </div>
              ))}

              {/* Punkt 8 (nederst): Førstehjælp med bevisknap */}
              <div className="flex items-center justify-between gap-3 p-3.5 bg-[#FAF8F5]/60 hover:bg-[#FAF8F5] border border-[#EDE6D8]/80 hover:border-[#5f7365]/30 transition-all">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-6 h-6 rounded-full bg-[#5f7365]/10 text-[#5f7365] flex items-center justify-center shrink-0">
                    <i className="fa-regular fa-shield-heart text-xs" aria-hidden="true" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-[#2C3B32] leading-snug">
                      Førstehjælp og akutberedskab
                    </p>
                    <p className="text-[11px] text-[#7A8E81] font-light">
                      HLR og hjertestarter (AED) certificering
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowCertificateModal(true)}
                  className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-medium text-[#5f7365] hover:text-[#2C3B32] bg-white hover:bg-[#FAF8F5] border border-[#EDE6D8] px-2.5 py-1 transition-colors cursor-pointer"
                  title="Se information og officielt kursusbevis"
                >
                  <i className="fa-regular fa-file-certificate text-[#5f7365] text-xs" aria-hidden="true" />
                  <span>Se bevis</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Concluding Assurance Footer */}
      {!isAboutPage && (
        <div className="bg-[#5f7365] border-t border-[#5f7365] px-6 sm:px-8 lg:px-10 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-white">
          <div className="flex items-center gap-2.5">
            <i className="fa-solid fa-shield-halved text-white text-sm shrink-0" aria-hidden="true" />
            <p className="leading-relaxed text-white/90">
              Jeg arbejder med udgangspunkt i gældende regler og faglige krav til psykologisk praksis og med fokus på kvalitet, sikkerhed og ansvarlighed.
            </p>
          </div>
          <span className="shrink-0 font-medium text-white tracking-wide self-end sm:self-center">
            Klinisk Psykolog cand.psych.aut.
          </span>
        </div>
      )}

      {/* Lightbox Modal for Certificate */}
      {showCertificateModal && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xs flex items-center justify-center p-4 sm:p-6"
          onClick={() => setShowCertificateModal(false)}
        >
          <div 
            className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl relative p-5 sm:p-6 space-y-4 border border-[#EDE6D8]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-[#EDE6D8] pb-3">
              <div className="space-y-0.5">
                <h3 className="font-serif text-lg font-bold text-[#5f7365] flex items-center gap-2">
                  <i className="fa-regular fa-file-certificate text-[#C6A868]" aria-hidden="true" />
                  <span>Officielt Førstehjælpsbevis</span>
                </h3>
                <p className="text-xs text-[#5A7364]">
                  Dansk Førstehjælpsråd og Dansk Folkehjælp — Marie Aagaard Larsen
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowCertificateModal(false)}
                className="text-[#5f7365] hover:text-black w-8 h-8 flex items-center justify-center text-lg hover:bg-gray-100 cursor-pointer transition-colors"
                aria-label="Luk visning"
              >
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>

            {/* Course Info Banner inside Modal */}
            <div className="bg-[#F6F2EA]/80 border border-[#EDE6D8] p-3 sm:p-3.5 text-xs sm:text-[13px] text-[#4E6254] flex flex-col md:flex-row md:items-center justify-between gap-2.5 md:gap-4 leading-relaxed">
              <div>
                <span className="font-semibold text-[#2C3B32]">Dato: </span>
                <span>3. oktober 2021</span>
              </div>
              <span className="hidden md:inline text-[#D6CEC1]">•</span>
              <div>
                <span className="font-semibold text-[#2C3B32]">Udsteder: </span>
                <span>Dansk Førstehjælpsråd og Dansk Folkehjælp</span>
              </div>
              <span className="hidden md:inline text-[#D6CEC1]">•</span>
              <div>
                <span className="font-semibold text-[#2C3B32]">Indhold: </span>
                <span>Førstehjælp ved hjertestop, HLR og betjening af hjertestarter (AED, 4 timer)</span>
              </div>
            </div>

            {/* Certificate Image View */}
            <div className="bg-[#FAF8F5] p-3 sm:p-4 border border-[#EDE6D8] flex justify-center">
              <img
                src={certificateSrc}
                alt="Officielt kursusbevis fra Dansk Førstehjælpsråd for Førstehjælp ved hjertestop udstedt til Marie Aagaard Larsen"
                className="max-h-[60vh] w-auto object-contain shadow-xs border border-gray-200"
              />
            </div>

            {/* Modal Footer */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#5A7364] pt-2 border-t border-[#EDE6D8]">
              <p>
                Gennemført 4 timers enkeltuddannelse i Førstehjælp ved hjertestop (HLR) og hjertestarter (AED).
              </p>
              <button
                type="button"
                onClick={() => setShowCertificateModal(false)}
                className="px-4 py-2 bg-[#5f7365] text-white font-medium hover:bg-[#4e6053] transition-colors cursor-pointer"
              >
                Luk visning
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}